import type { ArtifactStore } from '@railgun-community/wallet'
import { Buffer } from 'buffer'
import leveljs from 'level-js'
import { loadRailgunSdk } from '@web/utils/railgunSdk'
import levelup from 'levelup'

/**
 * Legacy IndexedDB-only artifact store: every artifact (wasm/vkey/zkey) is
 * fetched lazily by the SDK from its public IPFS gateway, SHA-256-verified, then
 * cached here. Used in contexts WITHOUT bundled artifacts — the mobile-web SPA
 * (no `/artifacts/` copied into the build) and tests. The extension uses the
 * hybrid `createBundledArtifactStore` instead.
 *
 * Kept in its own module (no `railgunCore` import) so the scan worker can use it
 * without pulling in chrome/ambire-network code (see railgunScanWorker.worker.ts
 * header — it intentionally avoids importing railgunCore).
 */
export async function createBrowserArtifactStore(artifactDbName: string): Promise<ArtifactStore> {
  const { ArtifactStore } = await loadRailgunSdk()
  const db = levelup(leveljs(artifactDbName) as Parameters<typeof levelup>[0])

  const getFile = async (path: string): Promise<Buffer> => {
    const data = await db.get(path)
    return Buffer.isBuffer(data) ? data : Buffer.from(data as ArrayBuffer)
  }

  const storeFile = async (
    _dir: string,
    path: string,
    item: string | Uint8Array
  ): Promise<void> => {
    const data = typeof item === 'string' ? Buffer.from(item, 'utf8') : Buffer.from(item)
    await db.put(path, data)
  }

  const fileExists = async (path: string): Promise<boolean> => {
    try {
      await db.get(path)
      return true
    } catch {
      return false
    }
  }

  return new ArtifactStore(getFile, storeFile, fileExists)
}
